/* eslint-disable max-len */
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useContext, useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useToasts } from 'react-toast-notifications'
import { Form, Spinner } from 'reactstrap'
import * as Yup from 'yup'

import { useDispatch } from 'react-redux'
import groupApi from '_api/group'
import uploadApi from '_api/upload'
import WithErrorBoundary from '_components/WithErrorBoundary'
import { updateGroup } from '_redux/modules/group'
import { getGroupIdSelected } from '_utils/localData'
import { GROUP_CURRENCIES } from '_utils/constant'
import { SelectIdGroupContext } from '../../context/SelectGroupContext'
import BannerImageSection from './components/BannerImageSection/BannerImageSection'
import DefaultItemViewSection from './components/DefaultItemViewSection/DefaultItemViewSection'
import ColorSchemeSection from './components/ColorSchemeSection/ColorSchemeSection'

import './style.scss'

const tabs = [
  { id: 'profile', label: 'Profile' },
  // { id: 'appearance', label: 'Appearance' },
  { id: 'group', label: 'Group' },
]

const SUPPORTED_TIME_ZONE = {
  value: 'Asia/Singapore',
  label: 'Asia / Singapore',
}

const profileValidationSchema = Yup.object().shape({
  groupName: Yup.string('Invalid name').required('Invalid name'),
  address: Yup.string('Invalid address').required('Invalid address'),
  groupSlug: Yup.string()
    .nullable()
    .notRequired()
    .test('len', 'Too short', (val) => !val || val.length >= 3),
  timeZone: Yup.string().nullable().notRequired(),
  currency: Yup.string().nullable().notRequired(),
  groupDescription: Yup.string().nullable().notRequired(),
  whatsapp: Yup.string()
    .nullable()
    .notRequired()
    .transform((value) => (value === '' ? null : value))
    .test('is-phone', 'Invalid phone number', (val) => {
      if (!val) return true
      const cleanVal = val.replace(/[\s-]/g, '')
      return /^\+?[0-9]{8,16}$/.test(cleanVal)
    }),
  instagram: Yup.string()
    .nullable()
    .notRequired()
    .transform((value) => (value === '' ? null : value))
    .url('Invalid Instagram URL'),
  facebook: Yup.string()
    .nullable()
    .notRequired()
    .transform((value) => (value === '' ? null : value))
    .url('Invalid Facebook URL'),
  website: Yup.string()
    .nullable()
    .notRequired()
    .transform((value) => (value === '' ? null : value))
    .url('Invalid website URL'),
  defaultItemView: Yup.string().nullable().notRequired(),
})

const appearanceValidationSchema = Yup.object().shape({
  bannerImageUrl: Yup.string().nullable().notRequired(),
  titleText: Yup.string().nullable().notRequired(),
  primaryColor: Yup.string().nullable().notRequired(),
  accentColor: Yup.string().nullable().notRequired(),
  pageBackgroundColor: Yup.string().nullable().notRequired(),
  topbarBackgroundColor: Yup.string().nullable().notRequired(),
  topbarTextColor: Yup.string().nullable().notRequired(),
  priceColor: Yup.string().nullable().notRequired(),
  itemTitleColor: Yup.string().nullable().notRequired(),
  defaultItemView: Yup.string().nullable().notRequired(),
})

const groupValidationSchema = Yup.object().shape({
  groupQuestion: Yup.string().nullable().notRequired(),
  passCode: Yup.string().nullable().notRequired(),
})

const profileResolver = yupResolver(profileValidationSchema)
const appearanceResolver = yupResolver(appearanceValidationSchema)
const groupResolver = yupResolver(groupValidationSchema)

const validationResolver = (data, context, options) => {
  if (context?.activeTab === 'appearance') {
    return appearanceResolver(data, context, options)
  }

  if (context?.activeTab === 'group') {
    return groupResolver(data, context, options)
  }

  return profileResolver(data, context, options)
}

const Settings = () => {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const { infoGroupSelected } = useContext(SelectIdGroupContext)
  const [activeTab, setActiveTab] = useState('profile')
  const [groupId, setGroupId] = useState(getGroupIdSelected())
  const [groupInfo, setGroupInfo] = useState()
  const [groupSetting, setGroupSetting] = useState()
  const [groupImg, setGroupImg] = useState('')
  const [groupImgUrl, setGroupUrl] = useState('')
  const [groupType, setGroupType] = useState()
  const [defaultRole, setDefaultRole] = useState()
  const [autoApproval, setAutoApproval] = useState()
  const [accessMemberList, setAccessMemberList] = useState()

  const methods = useForm({
    mode: 'all',
    resolver: validationResolver,
    context: { activeTab },
    defaultValues: {
      groupName: '',
      address: '',
      groupSlug: '',
      timeZone: SUPPORTED_TIME_ZONE.value,
      currency: '',
      groupDescription: '',
      whatsapp: '',
      instagram: '',
      facebook: '',
      website: '',
      groupQuestion: '',
      passCode: '',
      bannerImageUrl: '',
      titleText: '',
      primaryColor: '#009A97',
      accentColor: '#E8971A',
      pageBackgroundColor: '#F4F6F8',
      topbarBackgroundColor: '#009A97',
      topbarTextColor: '#FFFFFF',
      priceColor: '#009A97',
      itemTitleColor: '#009A97',
      defaultItemView: 'list',
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    // watch,
  } = methods

  // const groupUrlVal = watch('groupSlug') || ''
  // const isUrlAvailable = !errors.groupSlug && groupUrlVal.length >= 3
  // const isUrlTooShort = groupUrlVal.length > 0 && groupUrlVal.length < 3

  useEffect(() => {
    if (infoGroupSelected?.groupId !== undefined && infoGroupSelected?.groupId !== null) {
      setGroupId(infoGroupSelected.groupId)
    }
  }, [infoGroupSelected])

  useEffect(() => {
    let mounted = true

    const fetchGroup = async () => {
      if (groupId === undefined || groupId === null) {
        return
      }

      const { msgResp: groupData } = await groupApi.getById(groupId)
      if (mounted && groupData) {
        setGroupInfo(groupData)
      }
    }

    fetchGroup()

    return () => {
      mounted = false
    }
  }, [groupId])

  useEffect(() => {
    let mounted = true

    const fetchGroupSetting = async () => {
      if (groupId === undefined || groupId === null) {
        return
      }

      const { msgResp: groupSettingData } = await groupApi.getGroupSetting(groupId)
      if (mounted && groupSettingData) {
        setGroupSetting(groupSettingData)
      }
    }

    fetchGroupSetting()

    return () => {
      mounted = false
    }
  }, [groupId])

  useEffect(() => {
    if (!groupInfo) {
      return
    }

    setValue('groupName', groupInfo?.name || '')
    setValue('address', groupInfo?.address || '')
    setValue('groupSlug', groupInfo?.groupSlug || '')
    setValue('timeZone', groupInfo?.timeZone || SUPPORTED_TIME_ZONE.value)
    setValue('currency', groupInfo?.currency || '')
    setValue('groupDescription', groupInfo?.description || '')
    setValue('whatsapp', groupInfo?.whatsapp || '')
    setValue('instagram', groupInfo?.instagram || '')
    setValue('facebook', groupInfo?.facebook || '')
    setValue('website', groupInfo?.website || '')
    setValue('groupQuestion', groupInfo?.question || '')
    setValue('passCode', groupInfo?.passcode || '')
    setGroupUrl(groupInfo?.photoUrl || '')
    setGroupType(groupInfo?.public)
    setDefaultRole(groupInfo?.defaultRole)
    setAutoApproval(groupInfo?.autoApproval)
    setAccessMemberList(groupInfo?.accessMemberList)
  }, [groupInfo, setValue])

  useEffect(() => {
    if (!groupSetting) {
      return
    }

    setValue('bannerImageUrl', groupSetting?.bannerImageUrl || '')
    setValue('titleText', groupSetting?.titleText || '')
    setValue('primaryColor', groupSetting?.primaryColor || '#009A97')
    setValue('accentColor', groupSetting?.accentColor || '#E8971A')
    setValue('pageBackgroundColor', groupSetting?.pageBackgroundColor || '#F4F6F8')
    setValue('topbarBackgroundColor', groupSetting?.topbarBackgroundColor || '#009A97')
    setValue('topbarTextColor', groupSetting?.topbarTextColor || '#FFFFFF')
    setValue('priceColor', groupSetting?.priceColor || '#009A97')
    setValue('itemTitleColor', groupSetting?.itemTitleColor || '#009A97')
    setValue('defaultItemView', groupSetting?.defaultItemView === 2 ? 'tile' : 'list')
  }, [groupSetting, setValue])

  const onGroupImageUploaded = (event) => {
    const { files } = event.target
    if (!files?.length) {
      return
    }

    const img = files[0]
    setGroupUrl(URL.createObjectURL(img))
    setGroupImg(files)
  }

  const submitGroupProfile = async (data) => {
    const postData = {
      name: data.groupName,
      address: data.address,
      timeZone: data.timeZone,
      currency: data.currency,
      description: data.groupDescription,
      whatsapp: data.whatsapp,
      instagram: data.instagram,
      facebook: data.facebook,
      website: data.website,
      photoUrl: groupImgUrl,
      passcode: data.passCode,
      question: data.groupQuestion,
      public: groupType,
      defaultRole,
      autoApproval,
      accessMemberList,
    }

    if (groupImg?.length) {
      const { msgResp: res } = await uploadApi.uploadPhoto(groupImg)
      postData.photoUrl = res.url
    }

    await dispatch(updateGroup({ id: groupId, postData }))

    if (data.defaultItemView) {
      const settingPostData = {
        ...groupSetting,
        defaultItemView: data.defaultItemView === 'tile' ? 2 : 1,
      }
      await groupApi.updateGroupSetting({ groupId, postData: settingPostData })
      setGroupSetting((prevState) => ({
        ...prevState,
        defaultItemView: data.defaultItemView === 'tile' ? 2 : 1,
      }))
    }
  }

  const submitAppearance = async (data) => {
    const postData = {
      bannerImageUrl: data.bannerImageUrl || '',
      titleText: data.titleText || '',
      defaultItemView: data.defaultItemView === 'tile' ? 2 : 1,
      primaryColor: data.primaryColor,
      accentColor: data.accentColor,
      pageBackgroundColor: data.pageBackgroundColor,
      topbarBackgroundColor: data.topbarBackgroundColor,
      topbarTextColor: data.topbarTextColor,
      priceColor: data.priceColor,
      itemTitleColor: data.itemTitleColor,
    }

    if (data.bannerFile?.length) {
      const { msgResp: res } = await uploadApi.uploadPhoto(data.bannerFile)
      postData.bannerImageUrl = res.url
      setValue('bannerImageUrl', res.url)
      setValue('bannerFile', null)
    }

    await groupApi.updateGroupSetting({ groupId, postData })
    setGroupSetting((prevState) => ({
      ...prevState,
      ...postData,
    }))
  }

  const onSubmit = async (data) => {
    try {
      if (activeTab === 'appearance') {
        await submitAppearance(data)
      } else {
        await submitGroupProfile(data)
      }

      addToast('Update group successfully', { appearance: 'success', autoDismiss: true })
    } catch (error) {
      addToast(error.msgResp, { appearance: 'error', autoDismiss: true })
    }
  }

  const handleCancel = () => {
    methods.reset({
      groupName: groupInfo?.name || '',
      address: groupInfo?.address || '',
      groupSlug: groupInfo?.groupSlug || '',
      timeZone: groupInfo?.timeZone || SUPPORTED_TIME_ZONE.value,
      currency: groupInfo?.currency || '',
      groupDescription: groupInfo?.description || '',
      whatsapp: groupInfo?.whatsapp || '',
      instagram: groupInfo?.instagram || '',
      facebook: groupInfo?.facebook || '',
      website: groupInfo?.website || '',
      groupQuestion: groupInfo?.question || '',
      passCode: groupInfo?.passcode || '',
      bannerImageUrl: groupSetting?.bannerImageUrl || '',
      bannerFile: null,
      titleText: groupSetting?.titleText || '',
      defaultItemView: groupSetting?.defaultItemView === 2 ? 'tile' : 'list',
    })

    setGroupUrl(groupInfo?.photoUrl || '')
    setGroupImg('')
    setGroupType(groupInfo?.public)
    setDefaultRole(groupInfo?.defaultRole)
    setAutoApproval(groupInfo?.autoApproval)
    setAccessMemberList(groupInfo?.accessMemberList)

    addToast('Changes discarded', { appearance: 'warning', autoDismiss: true })
  }

  const renderOptionCard = ({ checked, title, description, onClick }) => (
    <button
      type='button'
      className={`settings-option-card ${checked ? 'is-active' : ''}`}
      onClick={onClick}
    >
      <span className={`settings-option-indicator ${checked ? 'is-active' : ''}`} />
      <span className='settings-option-copy'>
        <span className='settings-option-title'>{title}</span>
        <span className='settings-option-description'>{description}</span>
      </span>
    </button>
  )

  return (
    <div className='settings-page mb-3'>
      <div className='settings-tab-bar'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type='button'
            className={`settings-tab ${activeTab === tab.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)} className='settings-form'>
        {activeTab === 'profile' && (
          <React.Fragment>
            <div className='settings-section'>
              <div className='settings-section-header'>
                <span className='settings-section-title'>Profile</span>
              </div>

              <div className='settings-section-body'>
                <div className='settings-profile-hero'>
                  <label className='settings-photo-uploader'>
                    {groupImgUrl ? (
                      <img
                        src={groupImgUrl?.url || groupImgUrl}
                        alt='Group'
                        className='settings-photo-preview'
                      />
                    ) : (
                      <>
                        <span className='settings-photo-icon'>
                          <i className='icon-capture' />
                        </span>
                        <span className='settings-photo-text'>Add Photo</span>
                      </>
                    )}
                    <input type='file' accept='image/*' onChange={onGroupImageUploaded} hidden />
                  </label>

                  <div className='settings-photo-copy'>
                    <p>This photo appears across your group and event views.</p>
                    <label className='settings-secondary-btn mb-0'>
                      Upload Photo
                      <input type='file' accept='image/*' onChange={onGroupImageUploaded} hidden />
                    </label>
                  </div>
                </div>

                <div className='settings-grid'>
                  <div className='settings-field'>
                    <label htmlFor='groupName' style={{ textTransform: 'capitalize' }}>
                      Group Name <span className='req'>*</span>
                    </label>
                    <input
                      id='groupName'
                      className='settings-input'
                      type='text'
                      {...register('groupName')}
                    />
                    <small className='settings-error'>{errors?.groupName?.message}</small>
                  </div>
                  <div className='settings-field'>
                    <label htmlFor='address' style={{ textTransform: 'capitalize' }}>
                      Address <span className='req'>*</span>
                    </label>
                    <input
                      id='address'
                      className='settings-input'
                      type='text'
                      {...register('address')}
                    />
                    <small className='settings-error'>{errors?.address?.message}</small>
                  </div>
                </div>

                {/*
                <div className='settings-grid'>
                  <div className='settings-field'>
                    <label htmlFor='groupSlug'>
                      GROUP URL
                    </label>
                    <div className='settings-url-group'>
                      <div className='settings-url-prefix'>{window.location.origin}/</div>
                      <input
                        id='groupSlug'
                        className='settings-url-input'
                        type='text'
                        placeholder='your-group'
                        {...register('groupSlug')}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/[^a-zA-Z0-9-]/g, '')
                          setValue('groupSlug', e.target.value)
                        }}
                      />
                      <div
                        className={`settings-url-status ${
                          isUrlAvailable
                            ? 'is-available'
                            : isUrlTooShort
                            ? 'is-too-short'
                            : 'is-empty'
                        }`}
                      >
                        {isUrlAvailable && (
                          <div>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> <span>Available</span>
                          </div>
                        )}
                        {isUrlTooShort && (
                          <div>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> <span>Too short</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <a
                      href={`${window.location.origin}/${groupUrlVal}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='settings-url-preview'
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      <span>{window.location.origin}/{groupUrlVal}</span>
                    </a>
                  </div>
                </div>
                */}

                <div className='settings-grid'>
                  <div className='settings-field'>
                    <label htmlFor="timeZone" style={{ textTransform: 'capitalize' }}>Time Zone</label>
                    <select
                      id="timeZone"
                      className="settings-select"
                      {...register('timeZone')}
                    >
                      <option value={SUPPORTED_TIME_ZONE.value}>
                        {SUPPORTED_TIME_ZONE.label}
                      </option>
                    </select>
                    <small className='settings-error'>{errors?.timeZone?.message}</small>
                  </div>
                  <div className='settings-field'>
                    <label htmlFor="currency" style={{ textTransform: 'capitalize' }}>Currency</label>
                    <select
                      id="currency"
                      className="settings-select"
                      {...register('currency')}
                    >
                      <option value={GROUP_CURRENCIES[0].value} hidden>
                        {GROUP_CURRENCIES[0].label}
                      </option>
                      {GROUP_CURRENCIES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <small className='settings-error'>{errors?.currency?.message}</small>
                  </div>
                </div>

                <div className='settings-field' style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <label htmlFor='groupDescription' style={{ textTransform: 'capitalize' }}>
                    Headline / Description
                  </label>
                  <textarea
                    id='groupDescription'
                    className='settings-textarea'
                    rows='3'
                    {...register('groupDescription')}
                  />
                  <small className='settings-error'>{errors?.groupDescription?.message}</small>
                </div>

                <div className='settings-field'>
                  <label style={{ marginBottom: '12px' }}>Contact & Social</label>
                  <div className='settings-contact-row'>
                    <div className='settings-contact-icon' style={{ background: '#E8F8EF' }}>
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#27AE60' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                        <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.58 3.47 2 2 0 0 1 3.55 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 19z' />
                      </svg>
                    </div>
                    <div className='settings-contact-wrapper'>
                      <div className='settings-input-prefix'>
                        <span className='prefix'>WhatsApp</span>
                        <input className='settings-input' placeholder='+65 9123 4567' type='text' {...register('whatsapp')} />
                      </div>
                      {errors?.whatsapp?.message && (
                        <small className='settings-contact-error'>{errors.whatsapp.message}</small>
                      )}
                    </div>
                  </div>
                  <div className='settings-contact-row'>
                    <div className='settings-contact-icon' style={{ background: '#FEF2DC' }}>
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#E8971A' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                        <rect x='2' y='2' width='20' height='20' rx='5' />
                        <path d='M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z' />
                        <line x1='17.5' y1='6.5' x2='17.51' y2='6.5' />
                      </svg>
                    </div>
                    <div className='settings-contact-wrapper'>
                      <div className='settings-input-prefix'>
                        <span className='prefix'>Instagram</span>
                        <input className='settings-input' placeholder='@handle or URL' type='text' {...register('instagram')} />
                      </div>
                      {errors?.instagram?.message && (
                        <small className='settings-contact-error'>{errors.instagram.message}</small>
                      )}
                    </div>
                  </div>
                  <div className='settings-contact-row'>
                    <div className='settings-contact-icon' style={{ background: '#EBF5FB' }}>
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#3498DB' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                        <path d='M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z' />
                      </svg>
                    </div>
                    <div className='settings-contact-wrapper'>
                      <div className='settings-input-prefix'>
                        <span className='prefix'>Facebook</span>
                        <input className='settings-input' placeholder='https://facebook.com/yourgroup' type='text' {...register('facebook')} />
                      </div>
                      {errors?.facebook?.message && (
                        <small className='settings-contact-error'>{errors.facebook.message}</small>
                      )}
                    </div>
                  </div>
                  <div className='settings-contact-row'>
                    <div className='settings-contact-icon' style={{ background: '#F0F0F0' }}>
                      <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#888780' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                        <circle cx='12' cy='12' r='10' />
                        <line x1='2' y1='12' x2='22' y2='12' />
                        <path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
                      </svg>
                    </div>
                    <div className='settings-contact-wrapper'>
                      <div className='settings-input-prefix'>
                        <span className='prefix'>Website</span>
                        <input className='settings-input' placeholder='https://yourwebsite.com' type='text' {...register('website')} />
                      </div>
                      {errors?.website?.message && (
                        <small className='settings-contact-error'>{errors.website.message}</small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <DefaultItemViewSection /> */}
          </React.Fragment>
        )}

        {activeTab === 'group' && (
          <React.Fragment>
            <div className='settings-section'>
              <div className='settings-section-header'>
                <span className='settings-section-title'>Group Settings</span>
              </div>

              <div className='settings-section-body settings-section-body--compact'>
                <div className='settings-row'>
                  <div className='settings-row-label'>Group Type</div>
                  <div className='settings-row-options'>
                    {renderOptionCard({
                      checked: groupType === 0,
                      title: 'Private',
                      description:
                        'Events are private and only invited members can participate.',
                      onClick: () => setGroupType(0),
                    })}
                    {renderOptionCard({
                      checked: groupType === 1,
                      title: 'Public',
                      description: 'Anyone in co-bee can participate in Group-Buy.',
                      onClick: () => setGroupType(1),
                    })}
                  </div>
                </div>

                <div className='settings-divider' />

                <div className='settings-row'>
                  <div className='settings-row-label'>Default Permission</div>
                  <div className='settings-row-options'>
                    {renderOptionCard({
                      checked: defaultRole === 1,
                      title: 'Member',
                      description:
                        "Members don't have permission to host group-buy events by default.",
                      onClick: () => setDefaultRole(1),
                    })}
                    {renderOptionCard({
                      checked: defaultRole === 2,
                      title: 'Host',
                      description: 'Members can host group-buy events by default.',
                      onClick: () => setDefaultRole(2),
                    })}
                  </div>
                </div>

                <div className='settings-divider' />

                <div className='settings-row'>
                  <div className='settings-row-label'>Approval Type</div>
                  <div className='settings-row-options'>
                    {renderOptionCard({
                      checked: autoApproval === 0,
                      title: 'Admin Approval',
                      description: 'Requires admin review before a member can join.',
                      onClick: () => setAutoApproval(0),
                    })}
                    {renderOptionCard({
                      checked: autoApproval === 1,
                      title: 'Auto Approval',
                      description: 'Auto-admit members who have the passcode.',
                      onClick: () => setAutoApproval(1),
                    })}
                  </div>
                </div>

                <div className='settings-divider' />

                <div className='settings-row'>
                  <div className='settings-row-label'>Member Privacy</div>
                  <div className='settings-row-options'>
                    {renderOptionCard({
                      checked: accessMemberList === 1,
                      title: 'Visible',
                      description: 'All members can see who is in the group.',
                      onClick: () => setAccessMemberList(1),
                    })}
                    {renderOptionCard({
                      checked: accessMemberList === 0,
                      title: 'Hidden',
                      description: 'Members cannot see the full member list.',
                      onClick: () => setAccessMemberList(0),
                    })}
                  </div>
                </div>
              </div>
            </div>

            {autoApproval === 0 && (
              <div className='settings-section'>
                <div className='settings-section-header'>
                  <span className='settings-section-title'>Join Question</span>
                </div>

                <div className='settings-section-body'>
                  <div className='settings-field'>
                    <label htmlFor='groupQuestion'>Question to ask before joining</label>
                    <input
                      id='groupQuestion'
                      className='settings-input'
                      type='text'
                      placeholder='Question?'
                      {...register('groupQuestion')}
                    />
                    <small className='settings-error'>{errors?.groupQuestion?.message}</small>
                    <span className='settings-helper'>
                      New members must answer this before their request is reviewed.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {autoApproval === 1 && (
              <div className='settings-section'>
                <div className='settings-section-header'>
                  <span className='settings-section-title'>Passcode</span>
                </div>

                <div className='settings-section-body'>
                  <div className='settings-field'>
                    <label htmlFor='passCode'>Passcode for auto approval</label>
                    <input
                      id='passCode'
                      className='settings-input'
                      type='text'
                      {...register('passCode')}
                    />
                    <small className='settings-error'>{errors?.passCode?.message}</small>
                    <span className='settings-helper'>
                      Leave blank if you do not want passcode verification.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        )}

        {activeTab === 'appearance' && (
          <React.Fragment>
            <BannerImageSection />
            <ColorSchemeSection />
            <DefaultItemViewSection />
          </React.Fragment>
        )}

        <div className='settings-actions'>
          <button
            className='button-cancel'
            type='button'
            disabled={isSubmitting}
            onClick={handleCancel}
          >
            <svg
              width='12'
              height='12'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='1 4 1 10 7 10' />
              <path d='M3.51 15a9 9 0 1 0 .49-4.95' />
            </svg>
            Cancel
          </button>
          <button
            className='button-save'
            type='submit'
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner color='light' size='sm' />
            ) : (
              <>
                <svg
                  width='12'
                  height='12'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z' />
                  <polyline points='17 21 17 13 7 13' />
                  <polyline points='7 3 7 8 15 8' />
                </svg>
                Save Settings
              </>
            )}
          </button>
        </div>
      </Form>
    </FormProvider>
  </div>
  )
}

export default WithErrorBoundary(Settings)
